const COOKIE_NAME = "mb_cookie_consent";

function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.split("=").slice(1).join("="));
}

function setCookie(name, value, maxAgeSeconds = 60 * 60 * 24 * 365) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

export function getStoredConsent() {
  const raw = getCookie(COOKIE_NAME);
  if (!raw) return null;
  const parsed = safeParse(raw);
  return parsed && typeof parsed === "object" ? parsed : null;
}

export function saveConsent(settingsVersion, consent) {
  const payload = {
    version: Number(settingsVersion) || 1,
    necessary: true,
    analytics: Boolean(consent?.analytics),
    marketing: Boolean(consent?.marketing),
    accepted_at: new Date().toISOString(),
  };
  setCookie(COOKIE_NAME, JSON.stringify(payload));
  return payload;
}

export function clearConsent() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function isConsentValid(settings, consent) {
  if (!settings?.enabled) return true;
  if (!consent) return false;
  return Number(consent.version) === Number(settings.consent_version || 1);
}

function consentState(consent) {
  const analytics = Boolean(consent?.analytics);
  const marketing = Boolean(consent?.marketing);
  return {
    functionality_storage: "granted",
    security_storage: "granted",
    personalization_storage: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  };
}

function ensureGtagQueue() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

function injectScriptOnce(id, src) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

export function applyGoogleConsentMode(settings, consent) {
  if (typeof window === "undefined" || !settings?.enabled) return;
  const gaId = (settings.google_analytics_id || "").trim();
  const gtmId = (settings.google_tag_manager_id || "").trim();
  if (!gaId && !gtmId) return;

  ensureGtagQueue();
  if (!window.__mbConsentDefaultApplied) {
    window.gtag("consent", "default", consentState({ analytics: false, marketing: false }));
    window.__mbConsentDefaultApplied = true;
  }
  if (consent && isConsentValid(settings, consent)) {
    window.gtag("consent", "update", consentState(consent));
  }

  if (gaId) {
    injectScriptOnce("mb-google-analytics", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`);
    if (!window.__mbGoogleAnalyticsConfigured) {
      window.gtag("js", new Date());
      window.gtag("config", gaId, { send_page_view: false });
      window.__mbGoogleAnalyticsConfigured = true;
    }
  }

  if (gtmId) {
    injectScriptOnce("mb-google-tag-manager", `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`);
    if (!window.__mbGtmConfigured) {
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      window.__mbGtmConfigured = true;
    }
  }
}

function ensurePosthogStub() {
  if (typeof window === "undefined") return null;
  if (window.posthog && typeof window.posthog.init === "function") return window.posthog;
  const stub = window.posthog || [];
  if (!window.posthog) {
    window.posthog = stub;
  }
  if (!stub._i) {
    stub._i = [];
    stub.init = function init(i, s, a) {
      function g(t, e) {
        const o = e.split(".");
        if (o.length === 2) {
          t = t[o[0]];
          e = o[1];
        }
        t[e] = function posthogStub() {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      }
      let u = stub;
      if (a !== undefined) {
        u = stub[a] = [];
      } else {
        a = "posthog";
      }
      u.people = u.people || [];
      u.toString = function toString(t) {
        let e = "posthog";
        if (a !== "posthog") e += `.${a}`;
        if (!t) e += " (stub)";
        return e;
      };
      u.people.toString = function peopleToString() {
        return `${u.toString(1)}.people (stub)`;
      };
      const methods = "init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" ");
      for (let n = 0; n < methods.length; n += 1) {
        g(u, methods[n]);
      }
      stub._i.push([i, s, a]);
    };
    stub.__SV = 1;
  }
  return stub;
}

export function loadPosthogAnalytics(token) {
  if (typeof window === "undefined") return;
  const trimmed = (token || "").trim();
  if (!trimmed || window.__mbPosthogLoaded) return;
  const posthog = ensurePosthogStub();
  if (!posthog) return;
  if (!window.__mbPosthogDefaultApplied) {
    const apiHost = "https://us.i.posthog.com";
    window.__mbPosthogDefaultApplied = true;
    const scriptId = "mb-posthog";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `${apiHost.replace(".i.posthog.com", "-assets.i.posthog.com")}/static/array.js`;
      document.head.appendChild(script);
    }
    posthog.init(trimmed, {
      api_host: apiHost,
      person_profiles: "identified_only",
      session_recording: {
        recordCrossOriginIframes: true,
        capturePerformance: false,
      },
    });
    window.__mbPosthogLoaded = true;
  }
}

export function consentPayloadFromSettings(settings, currentConsent) {
  return {
    analytics: Boolean(currentConsent?.analytics && settings?.analytics_enabled),
    marketing: Boolean(currentConsent?.marketing && settings?.marketing_enabled),
  };
}
